import { ProjectApi } from 'api/ProjectApi';
import { SurveyQuestionsApi } from 'api/SurveyQuestionsApi';
import { PageMapper } from 'mapper/PageMapper';
import { Company } from 'model/Company';
import { Project } from 'model/Project';
import dayjs from 'dayjs';

import { CategoryMapper } from './CategoryMapper';
import { DistributionMapper } from './DistributionMapper';
import { ProjectLayoutMapper } from './ProjectLayoutMapper';

export class ProjectMapper {
  private pageMapper: PageMapper;
  private projectLayoutMapper: ProjectLayoutMapper;
  private distributionMapper: DistributionMapper;
  private categoryMapper: CategoryMapper;

  constructor(
    pageMapper: PageMapper,
    projectLayoutMapper: ProjectLayoutMapper,
    distributionMapper: DistributionMapper,
    categoryMapper: CategoryMapper
  ) {
    this.categoryMapper = categoryMapper;
    this.distributionMapper = distributionMapper;
    this.projectLayoutMapper = projectLayoutMapper;
    this.pageMapper = pageMapper;
  }

  deserialize(entry: ProjectApi.Entry): Project {
    return new Project({
      id: entry.id,
      name: entry.name,
      company:
        entry.company &&
        new Company({
          id: entry.company.id,
          name: entry.company.name,
          logo: entry.company.logo,
          brandColor: entry.company.brandColor,
        }),
      status: entry.status,
      kind: entry.kind,
      preferredLanguage: entry.preferredLanguage,
      liveStart: entry.liveStart && dayjs.utc(entry.liveStart),
      liveStop: entry.liveStop && dayjs.utc(entry.liveStop),
      pages: (entry.pages || [])
        .filter((page) => page.items.length > 0)
        .map((pageEntry) => this.pageMapper.deserialize(pageEntry)),
      layout: entry.layout && this.projectLayoutMapper.deserialize(entry.layout),
      distributions: (entry.distributions || [])
        .filter((distributionEntry) =>
          this.distributionMapper.supportsDeserialize(distributionEntry)
        )
        .map((distributionEntry) => this.distributionMapper.deserialize(distributionEntry)),
      categories: (entry.categories || []).map((categoryEntry) =>
        this.categoryMapper.deserialize(categoryEntry)
      ),
    });
  }

  // Pages of the questions contract are positional: they carry no id and no project-level data,
  // which is read from survey meta instead.
  deserializeQuestions(entry: SurveyQuestionsApi.Entry): Project {
    return new Project({
      id: entry.id,
      pages: (entry.pages || [])
        .filter((page) => page.items.length > 0)
        .map((pageEntry) => this.pageMapper.deserialize(pageEntry)),
    });
  }
}
